import { useEffect, useRef, useState } from 'react';
import {
  createCaseIntent,
  executeCaseIntent,
  type CaseAction,
  type CaseCommandResult,
  type CaseIntent,
  type CaseScope,
} from 'common/src/api/shipment-cases';

export function useCaseCommand(
  scope: CaseScope,
  identity: string,
  onSaved: (result: CaseCommandResult) => void
) {
  const active = useRef(identity);
  active.current = identity;
  const callbacks = useRef(onSaved);
  callbacks.current = onSaved;
  const lock = useRef(false);
  const pending = useRef<CaseIntent | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [frozen, setFrozen] = useState(false);
  useEffect(() => {
    active.current = identity;
    return () => {
      active.current = '';
    };
  }, [identity]);
  async function save(action: CaseAction, files: File[]) {
    if (lock.current || active.current !== identity) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      pending.current ||= createCaseIntent(scope, action, files);
      setFrozen(true);
      const result = await executeCaseIntent(pending.current);
      if (active.current === identity) {
        pending.current = null;
        setFrozen(false);
        callbacks.current(result);
      }
    } catch (err) {
      if (active.current !== identity) return;
      const code =
        typeof err === 'object' && err && 'code' in err ? String(err.code) : '';
      const message =
        typeof err === 'object' && err && 'message' in err
          ? String(err.message)
          : 'No se pudo completar la acción';
      const committed = Boolean(pending.current?.result);
      if (!committed && ['22023', '23514', '42501', 'PT409'].includes(code)) {
        pending.current = null;
        setFrozen(false);
      }
      setError(
        committed
          ? `La acción ya está registrada. Faltan documentos por subir. ${message}`
          : message
      );
    } finally {
      lock.current = false;
      if (active.current === identity) setBusy(false);
    }
  }
  return { save, busy, error, frozen };
}
